import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { IAuthUser } from '../../../model/interfaces/auth-user';
import { IUserRole } from '../../../model/interfaces/user-role';
import { AuthEntityService } from '../../auth-entity.service';
import { CustomerService } from '../../../customers/customer.service';
import { NotificationsService } from '@mt-ng2/notifications-module';
import { markAllFormFieldsAsTouched } from '@mt-ng2/common-functions';
import { DynamicFieldTypes } from '@mt-ng2/dynamic-form';
import { ActivatedRoute } from '@angular/router';
import { AuthUserDynamicConfig } from '../auth-user.dynamic-config';
import { DynamicField, DynamicLabel } from '@mt-ng2/dynamic-form';
import { FormGroup, UntypedFormGroup } from '@angular/forms';
import { IUser } from '../../../model/interfaces/user';
import { UserRoles } from '../../../model/UserRoles';
import { forkJoin, Subscription } from 'rxjs';
import { OrderService } from '../../../orders/order.service';
import { AuthService } from '@mt-ng2/auth-module';
import { ICustomer } from '../../../model/interfaces/customer';
import { ICarrier } from '../../../model/interfaces/carrier';
import { CarrierService } from '../../../carriers/services/carrier.service';

@Component({
    selector: 'app-auth-user-portal-access',
    templateUrl: './auth-user-portal-access.html',
    standalone: false
})
export class AuthUserPortalAccessComponent implements OnInit {
    @Input('user') user: IUser;
    @Input('canEdit') canEdit: boolean;
    @Output() updateVersion: EventEmitter<string> = new EventEmitter<string>();
    isEditing: boolean;
    isHovered: boolean;
    roles: IUserRole[];
    viewOnly: DynamicLabel[] = [];
    formObject: DynamicField[] = [];
    userId: number;
    formFactory: AuthUserDynamicConfig<IAuthUser>;
    doubleClickIsDisabled = false;
    configControls: string[] = ['Username', 'RoleId'];
    authUserRole: UserRoles;
    carriers: ICarrier[];
    customers: ICustomer[];
    roleIdChanges$: Subscription;

    constructor(
        private route: ActivatedRoute,
        private authEntitiyService: AuthEntityService,
        private notificationsService: NotificationsService,
        private orderService: OrderService,
        private authService: AuthService,
        private carrierService: CarrierService,
        private customerService: CustomerService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.userId = +this.route.snapshot.params.userId;
        const currentUser = this.authService.currentUser.getValue();
        forkJoin([
            this.orderService.getAuthUserRole(currentUser.AuthId),
            this.authEntitiyService.getAllRoles(),
            this.carrierService.getActive(),
            this.customerService.getActiveCustomers(),
        ]).subscribe(([userResponse, rolesResponse, carrierResponse, customerResponse]) => {
            this.authUserRole = userResponse;
            this.roles = rolesResponse.body;
            // Can change user role between Carrier <-> Customer
            if (this.user.AuthUser.RoleId === UserRoles.Carrier || this.user.AuthUser.RoleId === UserRoles.Customer) {
                this.roles = this.roles.filter((r) => r.Id === UserRoles.Carrier || r.Id === UserRoles.Customer);
                this.carriers = carrierResponse;
                this.customers = customerResponse;
            } else {
                // Don't allow any other roles to change to Carrier or Customer
                this.roles = this.roles.filter((r) => r.Id !== UserRoles.Carrier && r.Id !== UserRoles.Customer);
            }
            this.setConfig();
        });
    }

    setConfig(): void {
        this.formFactory = new AuthUserDynamicConfig<IAuthUser>(this.user.AuthUser, this.roles, this.configControls);
        const config = this.formFactory.getForUpdate();
        this.viewOnly = config?.viewOnly?.map((x) => new DynamicLabel(x));
        this.formObject = config.formObject?.map((x) => new DynamicField(x));

        if (this.user.AuthUser.RoleId === UserRoles.Customer) {
            // Add CarrierId dropdown if not present
            if (!this.formObject.find((o) => o.name === 'CarrierId')) {
                this.formObject.push(
                    new DynamicField({
                        formGroup: 'Contact',
                        label: 'Carrier',
                        name: 'CarrierId',
                        options: this.carriers,
                        type: { fieldType: DynamicFieldTypes.Select },
                        value: this.user?.Contact?.CarrierId || null,
                        disabled: true,
                    }),
                );
            }
        } else if (this.user.AuthUser.RoleId === UserRoles.Carrier) {
            // Add CustomerId dropdown if not present
            if (!this.formObject.find((o) => o.name === 'CustomerId')) {
                this.formObject.push(
                    new DynamicField({
                        formGroup: 'Contact',
                        label: 'Customer',
                        name: 'CustomerId',
                        options: this.customers,
                        type: { fieldType: DynamicFieldTypes.Select },
                        value: this.user?.Contact?.CustomerId || null,
                        disabled: true,
                    }),
                );
            }
        }

        // Disable RoleId change for non-super admin users
        if (this.userId > 0 && !this.isSuperAdminUser()) {
            this.formObject.find((o) => o.name === 'RoleId').disabled = true;
        }
    }

    isSuperAdminUser(): boolean {
        //current user is super admin
        return this.authUserRole === UserRoles.SuperAdmin;
    }

    formSubmitted(form: UntypedFormGroup): void {
        if (form.valid) {
            const raw = form.getRawValue();
            const username: string = raw.AuthUser.Username ?? '';
            if (username.length < 3) {
                this.notificationsService.error('Username must be at least 3 characters.');
                return;
            }
            if (!/^[a-zA-Z0-9_]+$/.test(username)) {
                this.notificationsService.error('Username can only contain letters, numbers, and underscores. No spaces or special characters.');
                return;
            }
            this.authEntitiyService
                .updatePortalAccess(
                    this.user.AuthUser.Id,
                    raw.AuthUser.Username as string,
                    raw.AuthUser.RoleId as number,
                    raw.Contact?.CustomerId as number,
                    raw.Contact?.CarrierId as number,
                )
                .subscribe({
                    next: () => {
                        this.success();
                        this.user.AuthUser.RoleId = raw.AuthUser.RoleId;
                        this.user.AuthUser.Username = raw.AuthUser.Username;
                        this.setConfig();
                        this.isEditing = false;
                    },
                    error: () => {
                        this.error();
                    },
                });
        } else {
            markAllFormFieldsAsTouched(form);
            this.error();
        }
    }

    formCreated(form: FormGroup): void {
        const isCustomer = this.user.AuthUser?.RoleId === UserRoles.Customer;
        const isCarrier = this.user.AuthUser?.RoleId === UserRoles.Carrier;

        if (this.user && (isCarrier || isCustomer) && this.isSuperAdminUser()) {
            this.roleIdChanges$ = form.controls.AuthUser.get('RoleId').valueChanges.subscribe((state) => {
                const contact = form.controls.Contact;

                if (isCustomer) {
                    const carrierCtrl = contact.get('CarrierId');
                    carrierCtrl[state === UserRoles.Carrier ? 'enable' : 'disable']();
                    if (state === UserRoles.Customer) carrierCtrl.setValue(null);
                }

                if (isCarrier) {
                    const customerCtrl = contact.get('CustomerId');
                    customerCtrl[state === UserRoles.Customer ? 'enable' : 'disable']();
                    if (state === UserRoles.Carrier) customerCtrl.setValue(null);
                }

                this.cdr.detectChanges();
            });
        }
    }

    changeAccess(): void {
        this.user.AuthUser.HasAccess = !this.user.AuthUser.HasAccess;
        this.authEntitiyService.changeAccess(this.user.AuthUser.Id, this.user.AuthUser.HasAccess).subscribe(() => {
            this.isEditing = false;
            this.success();
        });
    }
    edit(): void {
        this.isEditing = true;
    }

    cancel(): void {
        this.isEditing = false;
    }

    error(): void {
        this.notificationsService.error('Save failed. Please check the form and try again.');
    }

    success(): void {
        this.notificationsService.success('Saved Successfully');
    }
}
